import { inject, Injectable } from '@angular/core';
import {
    ConfigConstants,
    DateUtilities,
    MATERIAL_SOURCE_CODE,
    MaterialModel,
    MaterialService,
    NotificationCardState,
    NotificationUtilities,
    PaceUrlPipe,
    selectAppsTaskCode,
    selectCSMLTrackerLink,
    selectEnableRemoteWork,
    selectUseCsmlUrlWithParam,
    stateToNotificationIdModel,
    UriUtilitiesService,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { PaceNotificationCard, PaceNotificationSeverity, PaceStatusBadge } from '@evorait/pace-components';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { deriveStatusBadge } from '../../../core/services/status-badge/status-badge.model';

/** How badly the equipment is affected per effect code, which drives the card's border color. */
const SEVERITY_BY_EFFECT_CODE: Record<string, PaceNotificationSeverity> = {
    [ConfigConstants.EQUIPMENT_STATUS.TOTALLY_DOWN]: 'down',
    [ConfigConstants.EQUIPMENT_STATUS.APP_DOWN]: 'down',
    [ConfigConstants.EQUIPMENT_STATUS.PARTIALLY_DOWN]: 'partiallyDown',
    [ConfigConstants.EQUIPMENT_STATUS.APP_PARTIALLY_DOWN]: 'partiallyDown',
    [ConfigConstants.EQUIPMENT_STATUS.PARTIALLY_DOWN_CRITICAL]: 'partiallyDown',
    [ConfigConstants.EQUIPMENT_STATUS.WORKING]: 'working',
    [ConfigConstants.EQUIPMENT_STATUS.APP_WORKING]: 'working',
    [ConfigConstants.EQUIPMENT_STATUS.PARTIALLY_DOWN_NORMAL]: 'working',
};

/**
 * Turns the notification store's {@link NotificationCardState} into the fully rendered
 * {@link PaceNotificationCard} the presentational dashboard components consume: dates formatted,
 * labels translated, and the config-, value-help- and material-dependent fields resolved.
 *
 * This is where the view concerns the store slice deliberately leaves out are added, so both the
 * store projection and the components stay free of them.
 */
@Injectable({ providedIn: 'root' })
export class DashboardNotificationCardFactory {
    private readonly log: LogInterface = inject(LogInstanceToken);
    private readonly translate = inject(TranslateService);
    private readonly notificationUtilities = inject(NotificationUtilities);
    private readonly materialService = inject(MaterialService);
    private readonly uriUtilities = inject(UriUtilitiesService);
    private readonly store = inject(Store);
    private readonly paceUrlPipe = new PaceUrlPipe();

    private readonly appsTaskCode = this.store.selectSignal(selectAppsTaskCode);
    private readonly enableRemoteWork = this.store.selectSignal(selectEnableRemoteWork);
    private readonly csmlTrackerLink = this.store.selectSignal(selectCSMLTrackerLink);
    private readonly useCsmlUrlWithParam = this.store.selectSignal(selectUseCsmlUrlWithParam);

    public create(state: NotificationCardState): PaceNotificationCard {
        const identifier = stateToNotificationIdModel(state.notificationId);
        if (!state.hasMainEquipment) {
            this.log.error(
                `DashboardNotificationCardFactory: No Main Equipment available for notification ${identifier.toString()}`,
            );
        }

        const isApplicationSpecialist = state.taskGroup === this.appsTaskCode();
        const noOfOrderedMaterials = this.getNoOfOrderedMaterials(state, isApplicationSpecialist);

        return {
            // The card's id is the handle the host identifies the selected card by — see
            // `PaceNotificationCard.id`; `DashboardNgrxComponent` reads the identifier back out of it.
            id: identifier.toString(),
            weekday: DateUtilities.formatWeekday(state.start),
            startDateTime: this.getSingleOrMultiDayStartDateTimeString(state),
            multiDayWeekday: DateUtilities.formatWeekday(state.end),
            multiDayEndDateTime: state.isMultiDay
                ? `${DateUtilities.formatDate(state.end)} | ${DateUtilities.formatTime(state.end)}`
                : '',
            isMultiDay: state.isMultiDay,
            isEscalated: state.isEscalated,
            isCancelled: state.isCancelled.length > 0,
            isRemoteSupport: this.enableRemoteWork() && state.userStatus.includes(ConfigConstants.USER_STATUS.REMT),
            customerName: state.customerName,
            notificationType: this.notificationUtilities.getNotificationTypeDescriptionFor(
                state.notificationType,
                state.notificationId.qmNum,
                state.notificationId.maNum,
            ),
            tidnr: state.mainEquipmentTidnr,
            serialNumber: state.mainEquipmentSerialNumber,
            productName: state.mainEquipmentProductName,
            affectedEquipment: state.affectedEquipmentName,
            affectedEquipmentLabel: <string>this.translate.instant('CONFIRMATION_EQUIPMENT_AFFECTED_EQUIPMENT_LABEL'),
            clarifyTextHtml: this.paceUrlPipe.transform(state.clarifyText),
            trackerUrl: this.getCsmlTrackerUrl(state),
            trackAllOrdersLabel: <string>this.translate.instant('TRACK_ALL_ORDERS'),
            formsAvailable: this.notificationUtilities.isPaceFormsAvailable(state.formsCount)
                ? <string>this.translate.instant('DASHBOARD_ASSIGNMENTS_FORM_AVAILABLE_LABEL')
                : '',
            noOfParticipants: isApplicationSpecialist ? state.participantCount : 0,
            noOfOrderedMaterials,
            severity: SEVERITY_BY_EFFECT_CODE[state.effect] ?? 'unknown',
            badge: this.getBadge(state),
        };
    }

    private getBadge(state: NotificationCardState): PaceStatusBadge {
        const badgeInfo = deriveStatusBadge({
            statusCode: state.statusCode,
            isCancelled: state.isCancelled,
            start: state.start,
        });

        return {
            // badgeInfo is undefined for notifications in CRM status, which carry no badge
            text: badgeInfo ? <string>this.translate.instant(`NOTIFICATION_STATUS_BADGE_${badgeInfo.badge}_LABEL`) : '',
            styleClass: badgeInfo ? badgeInfo.badge.toLowerCase() : '',
            isEscalated: state.isEscalated,
            isCancelled: state.isCancelled.length > 0,
            isUnread: state.isUnread,
        };
    }

    /**
     * Start date and time, followed by the end time for a single-day notification — a multi-day
     * one shows its end on its own line instead.
     *
     * @param state the notification's card state
     * @returns the formatted date and time range
     */
    private getSingleOrMultiDayStartDateTimeString(state: NotificationCardState): string {
        const dateString = `${DateUtilities.formatDate(state.start)} | ${DateUtilities.formatTime(state.start)} - `;
        return state.isMultiDay ? dateString : dateString + DateUtilities.formatTime(state.end);
    }

    /**
     * The number of ordered materials must NOT include those added from Bootstock or Others.
     *
     * @param state the notification's card state
     * @param isApplicationSpecialist whether the notification belongs to an application specialist
     * @returns the number of ordered materials shown on the card
     */
    private getNoOfOrderedMaterials(state: NotificationCardState, isApplicationSpecialist: boolean): number {
        if (state.materialOrderNumbers.length === 0 || isApplicationSpecialist) {
            return 0;
        }

        const materials: MaterialModel[] = this.materialService.getAllMaterialsForNotification(
            stateToNotificationIdModel(state.notificationId),
        );
        return materials.filter(
            (material: MaterialModel) =>
                material.materialSource.sourceCode === MATERIAL_SOURCE_CODE.ORDERED &&
                material.showOnNotificationDetail,
        ).length;
    }

    private getCsmlTrackerUrl(state: NotificationCardState): string {
        return this.uriUtilities.getCSMLTrackerURL(
            state.materialOrderNumbers.map((orderNumber) => ({ orderNumber: { value: orderNumber } })),
            this.csmlTrackerLink(),
            false,
            this.useCsmlUrlWithParam(),
        );
    }
}
